import { useCallback, useMemo } from 'react';

import { type AnnouncementCommand, type AnnouncementCommandAction } from '@air/feature-announcements';

import { type AnnouncementSurfaceRendererProps } from '~/components/announcements/AnnouncementSurfaceRenderer';
import { useOpenBlankCanvasAnnouncementCommand } from '~/components/announcements/useOpenBlankCanvasAnnouncementCommand';
import { useOpenWorkspaceAgentCommand } from '~/components/announcements/useOpenWorkspaceAgentCommand';

type WorkspaceAnnouncementCommandProps = Pick<AnnouncementSurfaceRendererProps, 'onCommand' | 'supportedCommands'>;

/** Commands that only need the workspace, not a canvas or an open asset. */
export const useWorkspaceAnnouncementCommands = () => {
  const { canOpenWorkspaceAgent, openWorkspaceAgent } = useOpenWorkspaceAgentCommand();
  const { canOpenBlankCanvas, openBlankCanvas } = useOpenBlankCanvasAnnouncementCommand();

  const workspaceSupportedCommands = useMemo(() => {
    const commands: AnnouncementCommand[] = [];

    if (canOpenWorkspaceAgent) {
      commands.push('openAgent');
    }

    if (canOpenBlankCanvas) {
      commands.push('openBlankCanvas');
    }

    return commands;
  }, [canOpenBlankCanvas, canOpenWorkspaceAgent]);

  const runWorkspaceCommand = useCallback(
    (command: AnnouncementCommand, action: AnnouncementCommandAction, announcementId?: string) => {
      if (command === 'openAgent' && action.command === 'openAgent') {
        openWorkspaceAgent(action.params.prompt);
      }

      if (command === 'openBlankCanvas') {
        openBlankCanvas(announcementId);
      }
    },
    [openBlankCanvas, openWorkspaceAgent],
  );

  /** Spread onto `AnnouncementSurfaceRenderer` on surfaces whose only commands are workspace commands. */
  const workspaceCommandProps = useMemo<WorkspaceAnnouncementCommandProps>(
    () =>
      workspaceSupportedCommands.length > 0
        ? { onCommand: runWorkspaceCommand, supportedCommands: workspaceSupportedCommands }
        : {},
    [runWorkspaceCommand, workspaceSupportedCommands],
  );

  return { runWorkspaceCommand, workspaceCommandProps, workspaceSupportedCommands };
};
